import { AwsClient } from 'aws4fetch';
import { debug } from '../helpers/debug';
import { sanitizeStructuredInput } from '../helpers/formatHelpers';
import { supabase } from '../supabaseClient';

const DEFAULT_DEV_BUCKET = 'ghost-dev';
const DEFAULT_PROD_BUCKET = 'ghost-prod';
const DEFAULT_DEV_WORKER_URL = 'https://ghost-r2-dev.johnpirrie.workers.dev';
const DEFAULT_PROD_WORKER_URL = 'https://ghost-r2-prod.johnpirrie.workers.dev';

let r2Client = null;

const trimTrailingSlashes = (value = '') => String(value).replace(/\/+$/, '');

export const getWorkerBaseUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return DEFAULT_PROD_WORKER_URL;
  }
  return process.env.REACT_APP_R2_WORKER_URL || DEFAULT_DEV_WORKER_URL;
};

let _getWorkerSessionToken = async () => {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) {
    throw new Error('Unable to retrieve auth token for version worker');
  }
  return token;
};

export const getWorkerSessionToken = () => _getWorkerSessionToken();
export const __setWorkerSessionTokenProvider = (fn) => {
  _getWorkerSessionToken = fn;
};

let _requestWorker = async (url, options = {}) => {
  let token = await _getWorkerSessionToken();
  const sendRequest = async (accessToken) => {
    const headers = {
      ...(options.headers || {}),
      Authorization: `Bearer ${accessToken}`
    };
    return fetch(url, { ...options, headers, signal: options.signal });
  };

  let response = await sendRequest(token);
  if (response && response.status === 401) {
    const { data: refreshData } = await supabase.auth.refreshSession();
    const refreshedToken = refreshData?.session?.access_token;
    if (refreshedToken && refreshedToken !== token) {
      token = refreshedToken;
      response = await sendRequest(token);
    }
  }
  return response;
};

export const requestWorker = (url, options = {}) => _requestWorker(url, options);
export const __setRequestWorker = (fn) => {
  _requestWorker = fn;
};

function getR2Config(target = 'auto') {
  const accountId = process.env.REACT_APP_R2_ACCOUNT_ID;
  const accessKeyId = process.env.REACT_APP_R2_ACCESS_KEY_ID;
  const secretAccessKey = process.env.REACT_APP_R2_SECRET_ACCESS_KEY;

  if (!accountId || !accessKeyId || !secretAccessKey) {
    throw new Error('Missing Cloudflare R2 credentials. Set REACT_APP_R2_ACCOUNT_ID, REACT_APP_R2_ACCESS_KEY_ID, and REACT_APP_R2_SECRET_ACCESS_KEY.');
  }

  const envTarget = (process.env.REACT_APP_R2_TARGET || '').toLowerCase();
  const resolvedTarget = target === 'auto'
    ? (envTarget === 'prod' || process.env.NODE_ENV === 'production' ? 'prod' : 'dev')
    : target;

  const bucketName = resolvedTarget === 'prod'
    ? (process.env.REACT_APP_R2_BUCKET_PROD || DEFAULT_PROD_BUCKET)
    : (process.env.REACT_APP_R2_BUCKET_DEV || DEFAULT_DEV_BUCKET);

  const endpoint = (process.env.REACT_APP_R2_ENDPOINT || `https://${accountId}.r2.cloudflarestorage.com`).replace(/\/+$/, '');

  return {
    accountId,
    accessKeyId,
    secretAccessKey,
    bucketName,
    endpoint,
    target: resolvedTarget
  };
}

function getR2Client() {
  if (r2Client) return r2Client;
  const { accessKeyId, secretAccessKey } = getR2Config();
  r2Client = new AwsClient({
    accessKeyId,
    secretAccessKey,
    service: 's3'
  });
  return r2Client;
}

function encodeKeyPath(key = '') {
  return String(key)
    .split('/')
    .map(part => encodeURIComponent(part))
    .join('/');
}

export const buildVersionWorkerUrl = ({ projectId, docId, versionId }) => {
  if (!docId) throw new Error('buildVersionWorkerUrl requires a docId.');
  const base = trimTrailingSlashes(getWorkerBaseUrl());
  if (!base) {
    throw new Error('Unable to determine worker base URL.');
  }
  const encodedDocId = encodeURIComponent(String(docId));
  const path = versionId
    ? `/v1/docs/${encodedDocId}/versions/${encodeURIComponent(String(versionId))}`
    : `/v1/docs/${encodedDocId}/versions`;
  const url = new URL(`${base}${path}`);
  if (projectId) {
    url.searchParams.set('projectId', String(projectId));
  }
  return url.toString();
};

export const buildDocImagesWorkerUrl = ({ projectId, docId }) => {
  if (!docId) throw new Error('buildDocImagesWorkerUrl requires a docId.');
  const base = trimTrailingSlashes(getWorkerBaseUrl());
  if (!base) {
    throw new Error('Unable to determine worker base URL.');
  }
  const encodedDocId = encodeURIComponent(String(docId));
  const url = new URL(`${base}/v1/docs/${encodedDocId}/images`);
  if (projectId) {
    url.searchParams.set('projectId', String(projectId));
  }
  return url.toString();
};

export const buildImageWorkerUrl = ({ projectId, imageKey }) => {
  if (!projectId) throw new Error('buildImageWorkerUrl requires a projectId.');
  if (!imageKey) throw new Error('buildImageWorkerUrl requires an imageKey.');
  const base = trimTrailingSlashes(getWorkerBaseUrl());
  if (!base) {
    throw new Error('Unable to determine worker base URL.');
  }
  const encodedProjectId = encodeURIComponent(String(projectId));
  const encodedPath = encodeKeyPath(imageKey);
  return `${base}/v1/images/${encodedProjectId}/${encodedPath}`;
};

function buildObjectUrl(bucketName, key, endpoint) {
  return `${endpoint}/${bucketName}/${encodeKeyPath(key)}`;
}

function toMetaHeaders(metadata = {}) {
  const safeMetadata = sanitizeStructuredInput(metadata || {}, { maxDepth: 6, maxStringLength: 1000 }) || {};
  const entries = Object.entries(safeMetadata).filter(([, value]) => value !== undefined && value !== null);
  return Object.fromEntries(entries.map(([name, value]) => [`x-amz-meta-${String(name).toLowerCase()}`, String(value)]));
}

async function parseResponseBody(response) {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export function apiGetR2BucketName(target = 'auto') {
  const config = getR2Config(target);
  return config.bucketName;
}

export function apiBuildDocVersionKey(docId, versionId) {
  if (!docId || !versionId) {
    throw new Error('apiBuildDocVersionKey requires docId and versionId.');
  }
  return `docs/${docId}/versions/${versionId}.json`;
}

export async function apiPutR2Object(key, body, options = {}) {
  if (!key) throw new Error('apiPutR2Object requires a key.');

  const {
    target = 'auto',
    contentType = 'application/octet-stream',
    cacheControl,
    metadata = {}
  } = options;

  const config = getR2Config(target);
  const client = getR2Client();
  const url = buildObjectUrl(config.bucketName, key, config.endpoint);
  const headers = {
    'content-type': contentType,
    ...toMetaHeaders(metadata)
  };

  if (cacheControl) {
    headers['cache-control'] = cacheControl;
  }

  const response = await client.fetch(url, {
    method: 'PUT',
    headers,
    body
  });

  if (!response.ok) {
    const errorBody = await parseResponseBody(response);
    debug('[apiPutR2Object] error', { type: 'error' }, errorBody);
    throw new Error(`R2 PUT failed (${response.status}) for ${config.bucketName}/${key}`);
  }

  return {
    bucket: config.bucketName,
    key,
    etag: response.headers.get('etag') || null,
    r2_uri: `r2://${config.bucketName}/${key}`,
    url
  };
}

export async function apiGetR2Object(key, options = {}) {
  if (!key) throw new Error('apiGetR2Object requires a key.');

  const {
    target = 'auto',
    responseType = 'json'
  } = options;
  // Prefer direct R2 credentials, fall back to worker GET when missing
  try {
    const config = getR2Config(target);
    const client = getR2Client();
    const url = buildObjectUrl(config.bucketName, key, config.endpoint);
    const response = await client.fetch(url, { method: 'GET' });

    if (response.status === 404) return null;
    if (!response.ok) {
      const errorBody = await parseResponseBody(response);
      debug('[apiGetR2Object] error', { type: 'error' }, errorBody);
      throw new Error(`R2 GET failed (${response.status}) for ${config.bucketName}/${key}`);
    }

    let data;
    if (responseType === 'arrayBuffer') {
      data = await response.arrayBuffer();
    } else if (responseType === 'text') {
      data = await response.text();
    } else {
      data = await response.json();
    }

    return {
      bucket: config.bucketName,
      key,
      etag: response.headers.get('etag') || null,
      contentType: response.headers.get('content-type') || null,
      data
    };
  } catch (err) {
    // fallback to worker-admin GET
    try {
      const base = trimTrailingSlashes(getWorkerBaseUrl());
      const url = `${base}/v1/admin/object?key=${encodeURIComponent(String(key))}`;
      const res = await requestWorker(url, { method: 'GET' });
      if (res.status === 404) return null;
      if (!res.ok) {
        const body = await parseResponseBody(res);
        debug('[apiGetR2Object] worker-get error', { type: 'error' }, body);
        throw new Error(`Worker admin get failed (${res.status})`);
      }

      if (responseType === 'arrayBuffer') {
        const buf = await res.arrayBuffer();
        return { bucket: null, key, etag: res.headers.get('etag') || null, contentType: res.headers.get('content-type') || null, data: buf };
      }

      if (responseType === 'text') {
        const text = await res.text();
        return { bucket: null, key, etag: res.headers.get('etag') || null, contentType: res.headers.get('content-type') || null, data: text };
      }

      // default: try parse json
      let parsed = null;
      try { parsed = await res.json(); } catch { parsed = await res.text(); }
      return { bucket: null, key, etag: res.headers.get('etag') || null, contentType: res.headers.get('content-type') || null, data: parsed };
    } catch (err2) {
      debug('[apiGetR2Object] fallback error', { type: 'error' }, err2);
      throw err2;
    }
  }
}

export async function apiWriteR2Json(key, payload, options = {}) {
  return apiPutR2Object(key, JSON.stringify(payload), {
    ...options,
    contentType: 'application/json'
  });
}

export async function apiReadR2Json(key, options = {}) {
  const object = await apiGetR2Object(key, {
    ...options,
    responseType: 'json'
  });
  return object?.data ?? null;
}

export async function apiWriteDocVersionToR2({ projectId = null, docId, versionId, docJson, metadata = {}, target = 'auto' }) {
  const safeMetadata = sanitizeStructuredInput(metadata || {}, { maxDepth: 8, maxStringLength: 2000 }) || {};
  const key = apiBuildDocVersionKey(docId, versionId);
  const payload = {
    project_id: projectId,
    doc_id: docId,
    version_id: versionId,
    saved_at: new Date().toISOString(),
    doc: docJson,
    metadata: safeMetadata
  };

  const result = await apiWriteR2Json(key, payload, {
    target,
    metadata: {
      project_id: projectId,
      doc_id: docId,
      version_id: versionId,
      ...safeMetadata
    }
  });

  return {
    ...result,
    key
  };
}

export async function apiReadDocVersionFromR2({ docId, versionId, target = 'auto' }) {
  const key = apiBuildDocVersionKey(docId, versionId);
  return apiReadR2Json(key, { target });
}

/**
 * List objects in the configured R2 bucket using S3 ListObjectsV2
 * Returns an array of { key, size, lastModified, etag }
 */
export async function apiListR2Objects({ prefix = '', target = 'auto', maxKeys = 1000 } = {}) {
  // Try direct R2 access first; if config missing, fall back to worker admin endpoint
  try {
    const config = getR2Config(target);
    const client = getR2Client();
    const bucket = config.bucketName;
    const endpoint = config.endpoint.replace(/\/+$/,'');
    const url = `${endpoint}/${bucket}?list-type=2&prefix=${encodeURIComponent(String(prefix || ''))}&max-keys=${Number(maxKeys)}`;

    const res = await client.fetch(url, { method: 'GET' });
    if (!res.ok) {
      const body = await parseResponseBody(res);
      debug('[apiListR2Objects] error', { type: 'error' }, body);
      throw new Error(`R2 list failed (${res.status}) for ${bucket}`);
    }

    const text = await res.text();
  // Parse XML response for <Contents><Key>...</Key><LastModified>...</LastModified><Size>...</Size><ETag>...</ETag></Contents>
  const results = [];
  try {
    if (typeof DOMParser !== 'undefined') {
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'application/xml');
      const contents = Array.from(doc.getElementsByTagName('Contents') || []);
      for (const c of contents) {
        const key = c.getElementsByTagName('Key')?.[0]?.textContent || '';
        const lastModified = c.getElementsByTagName('LastModified')?.[0]?.textContent || null;
        const size = Number(c.getElementsByTagName('Size')?.[0]?.textContent || 0);
        const etag = c.getElementsByTagName('ETag')?.[0]?.textContent || null;
        results.push({ key, lastModified, size, etag });
      }
    } else {
      // Fallback simple regex parsing
      const re = /<Contents>\s*<Key>([\s\S]*?)<\/Key>\s*<LastModified>([\s\S]*?)<\/LastModified>\s*<ETag>([\s\S]*?)<\/ETag>\s*<Size>([\s\S]*?)<\/Size>\s*<\/Contents>/g;
      let m;
      while ((m = re.exec(text)) !== null) {
        results.push({ key: m[1], lastModified: m[2], etag: m[3], size: Number(m[4] || 0) });
      }
    }
  } catch (err) {
    debug('[apiListR2Objects] parse error', { type: 'error' }, err);
    throw err;
  }

  return results;
  } catch (err) {
    // Fallback to worker-admin list when direct R2 config is not available or fails
    try {
      const base = trimTrailingSlashes(getWorkerBaseUrl());
      const url = `${base}/v1/admin/list?prefix=${encodeURIComponent(String(prefix || ''))}&limit=${Number(maxKeys)}`;
      const res = await requestWorker(url, { method: 'GET' });
      if (!res.ok) {
        const body = await parseResponseBody(res);
        debug('[apiListR2Objects] worker-list error', { type: 'error' }, body);
        throw new Error(`Worker admin list failed (${res.status})`);
      }
      const json = await res.json();
      return json?.data || [];
    } catch (err2) {
      debug('[apiListR2Objects] fallback error', { type: 'error' }, err2);
      throw err2;
    }
  }
}

/**
 * Delete an object from R2 bucket
 */
export async function apiDeleteR2Object(key, { target = 'auto' } = {}) {
  if (!key) throw new Error('apiDeleteR2Object requires a key');
  // Prefer direct R2 credentials, fall back to worker admin delete when missing
  try {
    const config = getR2Config(target);
    const client = getR2Client();
    const url = buildObjectUrl(config.bucketName, key, config.endpoint);

    const res = await client.fetch(url, { method: 'DELETE' });
    if (!res.ok) {
      const body = await parseResponseBody(res);
      debug('[apiDeleteR2Object] error', { type: 'error' }, body);
      throw new Error(`R2 delete failed (${res.status}) for ${config.bucketName}/${key}`);
    }

    return true;
  } catch (err) {
    // Fallback to worker-admin delete
    try {
      const base = trimTrailingSlashes(getWorkerBaseUrl());
      const url = `${base}/v1/admin/object`;
      const res = await requestWorker(url, { method: 'DELETE', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ key }) });
      if (!res.ok) {
        const body = await parseResponseBody(res);
        debug('[apiDeleteR2Object] worker-delete error', { type: 'error' }, body);
        throw new Error(`Worker admin delete failed (${res.status})`);
      }
      return true;
    } catch (err2) {
      debug('[apiDeleteR2Object] fallback error', { type: 'error' }, err2);
      throw err2;
    }
  }
}
